import React from 'react'
import type { WorkflowNodeData } from '@/types/workflow'
import { SENIORITY_OPTIONS } from '@/lib/seniority'
import { Select } from '@/components/ui/Select'
import { Field } from '@/components/ui/Field'

interface ConditionConfigProps {
  data: WorkflowNodeData
  updateField: (field: string, value: any) => void
}

import { inputClass, inputStyle, sectionClass, infoBannerClass } from './_kit'
const conditionGroups = [
  {
    label: 'Email Engagement',
    options: [
      { value: 'email_opened', label: 'Email Opened' },
      { value: 'email_clicked', label: 'Link Clicked' },
      { value: 'email_replied', label: 'Email Replied (Any)' },
    ],
  },
  {
    label: 'Reply Sentiment',
    options: [
      { value: 'reply_positive', label: 'Replied Positively' },
      { value: 'reply_negative', label: 'Replied Negatively' },
      { value: 'reply_interested', label: 'Replied Interested' },
      { value: 'reply_not_interested', label: 'Replied Not Interested' },
      { value: 'reply_meeting_request', label: 'Requested Meeting' },
      { value: 'reply_meeting_time_provided', label: 'Proposed a Meeting Time' },
      { value: 'reply_out_of_office', label: 'Out of Office Reply' },
    ],
  },
  {
    label: 'Lead Properties',
    options: [
      { value: 'lead_score_above', label: 'Lead Score Above' },
      { value: 'lead_score_below', label: 'Lead Score Below' },
      { value: 'has_tag', label: 'Has Tag' },
      { value: 'missing_tag', label: 'Missing Tag' },
    ],
  },
  {
    label: 'Contact Properties',
    options: [
      { value: 'seniority_is', label: 'Seniority Is' },
      { value: 'company_size_is', label: 'Company Size Is' },
      { value: 'in_industry', label: 'In Industry' },
    ],
  },
  {
    label: 'Timing',
    options: [
      { value: 'days_since_last_email', label: 'Days Since Last Email' },
      { value: 'total_emails_sent_above', label: 'Total Emails Sent Above' },
    ],
  },
  {
    label: 'Custom',
    options: [
      { value: 'custom', label: 'Custom Expression' },
    ],
  },
]

const numericConditions = ['lead_score_above', 'lead_score_below', 'days_since_last_email', 'total_emails_sent_above']
const tagConditions = ['has_tag', 'missing_tag']
// Labels show the employee-count range each bucket covers (kept in sync with
// AddContactModal.tsx and backend/app/core/company_size.py's bucket_for_count).
const COMPANY_SIZE_OPTIONS = [
  { value: 'startup', label: '1-9' },
  { value: 'small', label: '10-50' },
  { value: 'medium', label: '51-200' },
  { value: 'large', label: '201-1000' },
  { value: 'enterprise', label: '1000+' },
]

const ConditionConfig: React.FC<ConditionConfigProps> = ({ data, updateField }) => {
  const condType = data.condition_type || ''
  // Only engagement conditions park and wait for a recipient action; static
  // ones (tag, score, custom expression) are decided the moment they are
  // reached, so a timeout would be meaningless on them. Mirrors
  // ENGAGEMENT_CONDITION_TYPES in backend/app/services/campaign_executor.py.
  const isEngagementCondition = condType.startsWith('email_') || condType.startsWith('reply_')

  const renderValueInput = () => {
    if (numericConditions.includes(condType)) {
      return (
        <Field label="Value">
          {(f) => (
            <input
              {...f}
              type="number"
              value={data.condition_value || ''}
              onChange={(e) => updateField('condition_value', e.target.value)}
              placeholder="Enter number..."
              className={inputClass} style={inputStyle}
            />
          )}
        </Field>
      )
    }
    if (tagConditions.includes(condType)) {
      return (
        <Field label="Tag Name">
          {(f) => (
            <input
              {...f}
              type="text"
              value={data.condition_value || ''}
              onChange={(e) => updateField('condition_value', e.target.value)}
              placeholder="e.g., vip"
              className={inputClass} style={inputStyle}
            />
          )}
        </Field>
      )
    }
    if (condType === 'seniority_is') {
      return (
        <Field label="Seniority Level">
          {(f) => (
            <Select
              {...f}
              value={data.condition_value || ''}
              onChange={(value) => updateField('condition_value', value)}
              options={SENIORITY_OPTIONS.map(s => ({ value: s.value, label: s.label }))}
              placeholder="Select..."
              className="w-full"
            />
          )}
        </Field>
      )
    }
    if (condType === 'company_size_is') {
      return (
        <Field label="Company Size">
          {(f) => (
            <Select
              {...f}
              value={data.condition_value || ''}
              onChange={(value) => updateField('condition_value', value)}
              options={COMPANY_SIZE_OPTIONS}
              placeholder="Select..."
              className="w-full"
            />
          )}
        </Field>
      )
    }
    if (condType === 'in_industry') {
      return (
        <Field label="Industry">
          {(f) => (
            <input
              {...f}
              type="text"
              value={data.condition_value || ''}
              onChange={(e) => updateField('condition_value', e.target.value)}
              placeholder="e.g., Technology"
              className={inputClass} style={inputStyle}
            />
          )}
        </Field>
      )
    }
    if (condType === 'custom') {
      return (
        <Field label="Custom Expression">
          {(f) => (
            <input
              {...f}
              type="text"
              value={data.condition_value || ''}
              onChange={(e) => updateField('condition_value', e.target.value)}
              placeholder="Enter expression..."
              className={inputClass} style={inputStyle}
            />
          )}
        </Field>
      )
    }
    return null
  }

  return (
    <div className={sectionClass}>
      <Field label="Label">
        {(f) => (
          <input
            {...f}
            type="text"
            value={data.label || ''}
            onChange={(e) => updateField('label', e.target.value)}
            placeholder="e.g., Email Opened?"
            className={inputClass} style={inputStyle}
          />
        )}
      </Field>
      <Field label="Condition Type">
        {(f) => (
          <Select
            {...f}
            value={condType}
            onChange={(value) => {
              updateField('condition_type', value)
              updateField('condition_value', '')
            }}
            groups={conditionGroups}
            placeholder="Select condition..."
            className="w-full"
          />
        )}
      </Field>
      {renderValueInput()}
      {isEngagementCondition && (
        <Field label="How long to wait for it">
          {() => (
            <div className="flex gap-2">
              <input
                type="number"
                min={1}
                value={data.wait_timeout_value ?? 3}
                onChange={(e) => updateField('wait_timeout_value', Number(e.target.value))}
                className={inputClass}
                style={{ ...inputStyle, width: '6rem' }}
              />
              <Select
                value={data.wait_timeout_unit || 'days'}
                onChange={(value) => updateField('wait_timeout_unit', value)}
                options={[
                  { value: 'minutes', label: 'minutes' },
                  { value: 'hours', label: 'hours' },
                  { value: 'days', label: 'days' },
                ]}
                className="flex-1"
              />
            </div>
          )}
        </Field>
      )}
      <div className={infoBannerClass}>
        <p className="text-xs">
          The <strong>Yes</strong> path runs when the condition is met. The <strong>No</strong> path runs otherwise.
          {isEngagementCondition && (
            <>
              {' '}This one waits for the recipient to act: if they do nothing within the window above,
              the <strong>No</strong> path runs. An open or reply arriving after that is too late to
              change the branch, so set the window to as long as you would realistically wait.
            </>
          )}
        </p>
      </div>
    </div>
  )
}

export default ConditionConfig
